import { drawCanvasIcon, drawFilterLinesMark } from "./canvas-icons";
import { pickContrastingColor } from "./color-contrast";
import { volcaColor } from "./spreadsheet-theme";

const labelWidthCache = new Map<string, number>();

const measureLabelWidth = (
  ctx: CanvasRenderingContext2D,
  label: string,
  font: string,
) => {
  const key = `${font}\u0000${label}`;
  const cached = labelWidthCache.get(key);
  if (cached !== undefined) return cached;

  ctx.font = font;
  const width = ctx.measureText(label).width;
  labelWidthCache.set(key, width);
  return width;
};

const CHIP_HEIGHT_PX = 21;
const CHIP_RADIUS_PX = 5;
const CHIP_ICON_PX = 15;
const IDLE_MARK_PX = 13;
const HEADER_TEXT_PADDING_PX = 8;
const IDLE_MARK_GAP_PX = 10;
const EDGE_INSET_PX = 5;
const TWO_ICON_MIN_WIDTH_PX = 128;

export interface ColumnControlState {
  filtered: boolean;
  sortDirection: "asc" | "desc" | null;
  textColor: string;
  backgroundColor: string;
  label: string;
  font: string;
}

export const transparentVersionOf = (color: string): string => {
  const hex = color.trim();
  if (/^#[0-9a-f]{6}$/i.test(hex)) return `${hex}00`;
  if (/^#[0-9a-f]{3,4}$/i.test(hex)) {
    const r = hex.charAt(1);
    const g = hex.charAt(2);
    const b = hex.charAt(3);
    return `#${r}${r}${g}${g}${b}${b}00`;
  }
  if (/^#[0-9a-f]{8}$/i.test(hex)) return `${hex.slice(0, 7)}00`;

  const channels = hex.match(/^rgba?\(([^)]+)\)$/i)?.[1];
  if (channels) {
    const [r, g, b] = channels.split(",").map((part) => part.trim());
    return `rgba(${r}, ${g}, ${b}, 0)`;
  }

  return "rgba(255, 255, 255, 0)";
};

const fadeOutLabelBehind = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
  markerLeft: number,
  backgroundColor: string,
): void => {
  const fadeWidth = 12;
  const fadeStart = Math.max(rect.x, markerLeft - fadeWidth);
  const gradient = ctx.createLinearGradient(fadeStart, 0, markerLeft, 0);
  gradient.addColorStop(0, transparentVersionOf(backgroundColor));
  gradient.addColorStop(1, backgroundColor);

  ctx.fillStyle = gradient;
  ctx.fillRect(fadeStart, rect.y + 1, markerLeft - fadeStart, rect.height - 2);

  ctx.fillStyle = backgroundColor;
  ctx.fillRect(
    markerLeft,
    rect.y + 1,
    rect.x + rect.width - markerLeft,
    rect.height - 2,
  );
};

const roundedRectPath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void => {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
};

export const drawColumnControlMarker = (
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; width: number; height: number },
  {
    filtered,
    sortDirection,
    textColor,
    backgroundColor,
    label,
    font,
  }: ColumnControlState,
): void => {
  const active = filtered || sortDirection !== null;
  const centerY = Math.round(rect.y + rect.height / 2);
  const right = rect.x + rect.width - EDGE_INSET_PX;

  ctx.save();
  ctx.beginPath();
  ctx.rect(rect.x, rect.y, rect.width, rect.height);
  ctx.clip();

  if (!active) {
    const labelWidth = measureLabelWidth(ctx, label, font);
    const freeSpace =
      rect.width - labelWidth - HEADER_TEXT_PADDING_PX * 2 - IDLE_MARK_PX;
    if (freeSpace < IDLE_MARK_GAP_PX) {
      ctx.restore();
      return;
    }

    drawFilterLinesMark(ctx, {
      centerX: right - IDLE_MARK_PX / 2,
      centerY,
      width: IDLE_MARK_PX,
      color: textColor,
      alpha: 0.6,
    });
    ctx.restore();
    return;
  }

  const showBoth =
    filtered && sortDirection !== null && rect.width >= TWO_ICON_MIN_WIDTH_PX;
  const chipHeight = Math.min(CHIP_HEIGHT_PX, rect.height - 4);
  const chipWidth = showBoth ? 38 : 24;
  const chipX = right - chipWidth;
  const chipY = Math.round(centerY - chipHeight / 2);

  const chipFill = pickContrastingColor(backgroundColor, [
    volcaColor("--sand-2"),
    volcaColor("--sand-12"),
  ]);
  const chipInk = pickContrastingColor(chipFill, [backgroundColor], 3);

  fadeOutLabelBehind(ctx, rect, chipX - 3, backgroundColor);

  ctx.fillStyle = chipFill;
  roundedRectPath(ctx, chipX, chipY, chipWidth, chipHeight, CHIP_RADIUS_PX);
  ctx.fill();

  ctx.globalAlpha = 0.25;
  ctx.strokeStyle = chipInk;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.globalAlpha = 1;

  const sortIcon = sortDirection === "asc" ? "barsArrowUp" : "barsArrowDown";

  if (showBoth) {
    drawCanvasIcon(ctx, "funnel", {
      centerX: chipX + chipWidth * 0.31,
      centerY,
      size: CHIP_ICON_PX - 1,
      color: chipInk,
    });
    drawCanvasIcon(ctx, sortIcon, {
      centerX: chipX + chipWidth * 0.69,
      centerY,
      size: CHIP_ICON_PX - 1,
      color: chipInk,
    });
  } else if (filtered) {
    drawCanvasIcon(ctx, "funnel", {
      centerX: chipX + chipWidth / 2,
      centerY,
      size: CHIP_ICON_PX,
      color: chipInk,
    });
  } else {
    drawCanvasIcon(ctx, sortIcon, {
      centerX: chipX + chipWidth / 2,
      centerY,
      size: CHIP_ICON_PX,
      color: chipInk,
    });
  }

  ctx.restore();
};
